import {
  Client,
  Conversation,
  CONVERSATION_STORAGE_PREFIX,
  ConversationContext,
  StoredConversation,
} from '@stripe-internal/conversations-client';
import {useCallback, useContext, useEffect} from 'react';
import {
  useClient,
  useWidgetVisibility,
  Visibility,
} from '@stripe-internal/conversations-ui';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import {
  Config,
  EligibleChannelsEndpointResponseChannelRequestACall,
  Profile,
  RequestACallCodesEndpointResponseSuccess,
} from '@stripe-internal/conversations-shared-dependencies';
import {
  Screen,
  ScreenName,
  TransitionOptions,
  useScreen,
} from './Providers/ScreenProvider';
import {useMountEffect} from './useMountEffect';
import {ConfigContext} from './Providers/ConfigProvider';
import {
  getInProgressStoredConversation,
  getStoredConversation,
} from './helpers/getStoredConversation';
import {getWaitTime} from './components/ChannelSelector/getDisplayChannels';
import {useProfile} from './Providers/ProfileProvider';
import {useApi} from './Providers/ApiProvider/ApiProvider';
import {Api} from './Providers/ApiProvider/api/api';
import {useHandleAuthFailure} from './Providers/ProfileProvider/HandleAuthFailureApiProvider';

async function pendingRequestACallChannel(
  api: Api,
  entryPoint: string | undefined,
  audience: string | undefined,
): Promise<EligibleChannelsEndpointResponseChannelRequestACall | null> {
  if (!entryPoint || !audience) {
    return Promise.resolve(null);
  }

  return api
    .fetchEligibleChannels(entryPoint, audience)
    .then((resp) => {
      return resp.channels.find((channel) => {
        return channel.type === 'request_a_call' && channel.pending;
      }) as EligibleChannelsEndpointResponseChannelRequestACall | null;
    })
    .catch(() => {
      return null;
    });
}

async function pendingRequestACallChannelV2(
  api: Api,
  entryPoint: string | undefined,
): Promise<RequestACallCodesEndpointResponseSuccess | null> {
  if (!entryPoint) {
    return Promise.resolve(null);
  }

  return api
    .fetchRequestACallCodes(entryPoint)
    .then((resp) => {
      if (resp.type !== 'success' || !resp.pending) {
        return null;
      }
      return resp;
    })
    .catch(() => {
      return null;
    });
}

async function noDepsCheckPendingRac(
  api: Api,
  config: Config,
  profile: Profile,
  transitionTo: (screen: Screen, opts?: Partial<TransitionOptions>) => void,
) {
  const {
    audience,
    entrypoint: entryPoint,
    featureFlags,
    requiresSession,
  } = config;

  const {visitor} = profile;

  // don't try to do any transitions if we require a session
  // and don't have one yet
  if (requiresSession && visitor.sessionType === 'anonymous') {
    return;
  }

  if (
    featureFlags.universalHelpUseInlineRacEmailForm ||
    featureFlags.racInProgressV2
  ) {
    const pendingRAC = await pendingRequestACallChannelV2(api, entryPoint);

    if (pendingRAC) {
      transitionTo(
        {
          name: ScreenName.RequestACallConfirmationInline,
          props: {
            name: pendingRAC?.pendingRequest?.name || visitor.displayName || '',
            phoneNumber:
              pendingRAC?.pendingRequest?.phoneNumber ||
              visitor.phoneNumber ||
              '',
            message: pendingRAC?.pendingRequest?.message || '',
          },
        },
        {open: true},
      );
    }
  } else {
    const racChannel = await pendingRequestACallChannel(
      api,
      entryPoint,
      audience,
    );
    if (racChannel) {
      transitionTo(
        {
          name: ScreenName.RequestACallConfirmation,
          props: {
            name: visitor.displayName || '',
            phoneNumber: visitor.phoneNumber || '',
            waitTime: getWaitTime(racChannel, false),
          },
        },
        {open: true},
      );
    }
  }
}

const debouncedCheckPendingRac = debounce(noDepsCheckPendingRac, 500);

async function noDepsMaybeTransition(
  api: Api,
  config: Config,
  profile: Profile,
  client: Client,
  currentScreen: Screen,
  transitionTo: (screen: Screen, opts?: Partial<TransitionOptions>) => void,
) {
  const {audience, entrypoint: entryPoint, requiresSession} = config;
  const {visitor, featureFlags} = profile;
  const {racInProgressV2} = featureFlags;

  // Don't try to do any transitions if we require a session
  // and don't have one yet.
  //
  // It's worth noting that this means if we don't have the
  // session yet, it won't auto-open if there's an ongoing
  // conversation. In a UH conversation, that would lead to an
  // error screen if we did without a session.
  //
  // If you manually open the widget, it will still auto-resume,
  // including doing session polling when necessary.
  //
  // The scenario is unlikely to happen anyway though as I
  // don't know how you would have an ongoing conversation
  // without a session if it is required.
  if (requiresSession && visitor.sessionType === 'anonymous') {
    return;
  }

  if (currentScreen.name === ScreenName.Chat) {
    return;
  }

  let storedConversation: StoredConversation | null = null;
  if (!racInProgressV2) {
    storedConversation = getStoredConversation();
    const context = storedConversation?.data?.context;

    if (context?.entryPoint !== entryPoint || context?.userId !== visitor.id) {
      debouncedCheckPendingRac(api, config, profile, transitionTo);
      return;
    }
  }

  const result = await getInProgressStoredConversation({
    client,
    audience,
    entryPoint,
    storedConversation,
  });

  if (
    !result ||
    'error' in result.inProgressResponse ||
    !result.inProgressResponse.data.inProgress
  ) {
    Conversation.clearStorage({all: true});
    if (!racInProgressV2) {
      debouncedCheckPendingRac(api, config, profile, transitionTo);
    }
    return;
  }

  const {storedConversation: stored, inProgressResponse} = result;

  let conversationId: string | undefined = stored?.id;
  if (inProgressResponse.data.conversationId) {
    conversationId = inProgressResponse.data.conversationId;
  }

  let context: ConversationContext | undefined;
  if (stored && conversationId === stored.id) {
    context = {...stored.data?.context};
  }

  let resumeVisibility = config.resumeVisibilityState as Visibility | undefined;

  if (context?.visibility === 'closed') {
    return;
  } else if (context?.visibility && !resumeVisibility) {
    resumeVisibility = context.visibility as Visibility;
  }

  transitionTo(
    {
      name: ScreenName.Chat,
      props: {
        conversationId,
        context,
        inProgressResponse,
      },
    },
    {
      open: true,
      visibility: resumeVisibility,
    },
  );
}

const debouncedMaybeTransition = debounce(noDepsMaybeTransition, 1000);
const throttledMaybeTransition = throttle(noDepsMaybeTransition, 1000, {
  leading: true,
  trailing: false,
});

export const useResumeConversation = () => {
  const config = useContext(ConfigContext);

  const {profile} = useProfile();
  const {needsAuth} = useHandleAuthFailure();

  const api = useApi();

  const client = useClient();
  const {screen, transitionTo} = useScreen();
  const {visibility} = useWidgetVisibility();

  const maybeTransitionToConversation = useCallback(
    async (mode: 'debounce' | 'throttle' = 'debounce') => {
      if (needsAuth) {
        return;
      }

      if (mode === 'throttle') {
        // on load, we want to fire our check immediately
        throttledMaybeTransition(
          api,
          config,
          profile,
          client,
          screen,
          transitionTo,
        );
        return;
      }

      debouncedMaybeTransition(
        api,
        config,
        profile,
        client,
        screen,
        transitionTo,
      );
    },
    [api, config, profile, needsAuth, client, screen, transitionTo],
  );

  useMountEffect(() => {
    // check on-load if there is a stored conversation
    maybeTransitionToConversation('throttle');
  });

  useEffect(() => {
    // same as above, but check when the widget is opened
    if (visibility === 'open') {
      maybeTransitionToConversation();
    }
  }, [maybeTransitionToConversation, visibility]);

  useEffect(() => {
    // register a listener for when a conversation is stored
    // so that if we're open, but not on the chat screen, we
    // can transition to it
    const listener = (e: StorageEvent) => {
      if (!e.key?.startsWith(CONVERSATION_STORAGE_PREFIX) || !e.newValue) {
        return;
      }

      // only check for a new stored conversation from
      // another tab if the widget is already open
      if (visibility === 'closed') {
        return;
      }

      maybeTransitionToConversation();
    };

    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
  }, [maybeTransitionToConversation, visibility]);
};
